import { PublicKey } from "@solana/web3.js";
import { ALT_PROGRAM_ID, MPL_TOKEN_METADATA_PROGRAM_ID, SEEDS } from "./constants";
import { PROGRAM_ID } from "../env";
import { ProgramSdkError } from "./error";
import { toU64Le, toU8 } from "./utils";

/**
 * Derive Exchange PDA (singleton central state)
 * Seeds: ["central_state"]
 */
export function getExchangePda(
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.CENTRAL_STATE)],
    programId
  );
}

/**
 * Derive Market PDA
 * Seeds: ["market", market_id (u64 little-endian)]
 */
export function getMarketPda(
  marketId: bigint,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.MARKET), toU64Le(marketId)],
    programId
  );
}

/**
 * Derive Condition Tombstone PDA
 * Seeds: ["condition", condition_id (32 bytes)]
 */
export function getConditionTombstonePda(
  conditionId: Buffer,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  if (conditionId.length !== 32) {
    throw ProgramSdkError.invalidDataLength("conditionId", 32, conditionId.length);
  }
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.CONDITION), conditionId],
    programId
  );
}

/**
 * Derive Vault PDA (deposit token storage)
 * Seeds: ["market_deposit_token_account", deposit_mint, market]
 */
export function getVaultPda(
  depositMint: PublicKey,
  market: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.MARKET_DEPOSIT_TOKEN_ACCOUNT),
      depositMint.toBuffer(),
      market.toBuffer(),
    ],
    programId
  );
}

/**
 * Derive Mint Authority PDA (signs mint/burn operations)
 * Seeds: ["market_mint_authority", market]
 */
export function getMintAuthorityPda(
  market: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.MARKET_MINT_AUTHORITY), market.toBuffer()],
    programId
  );
}

/**
 * Derive Conditional Mint PDA (per outcome)
 * Seeds: ["conditional_mint", market, deposit_mint, outcome_index (u8)]
 */
export function getConditionalMintPda(
  market: PublicKey,
  depositMint: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.CONDITIONAL_MINT),
      market.toBuffer(),
      depositMint.toBuffer(),
      toU8(outcomeIndex),
    ],
    programId
  );
}

/**
 * Derive all Conditional Mint PDAs for a market
 */
export function getAllConditionalMintPdas(
  market: PublicKey,
  depositMint: PublicKey,
  numOutcomes: number,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number][] {
  const pdas: [PublicKey, number][] = [];
  for (let i = 0; i < numOutcomes; i++) {
    pdas.push(getConditionalMintPda(market, depositMint, i, programId));
  }
  return pdas;
}

/**
 * Derive the Metaplex metadata PDA for a conditional mint.
 * Seeds: ["metadata", MPL_TOKEN_METADATA_PROGRAM_ID, conditional_mint]
 * Program: MPL_TOKEN_METADATA_PROGRAM_ID
 */
export function getMplMetadataPda(
  conditionalMint: PublicKey
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.MPL_METADATA),
      MPL_TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      conditionalMint.toBuffer(),
    ],
    MPL_TOKEN_METADATA_PROGRAM_ID
  );
}

/**
 * Derive Order Status PDA
 * Seeds: ["order_status", order_hash (32 bytes)]
 */
export function getOrderStatusPda(
  orderHash: Buffer,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  if (orderHash.length !== 32) {
    throw ProgramSdkError.invalidDataLength("orderHash", 32, orderHash.length);
  }
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.ORDER_STATUS), orderHash],
    programId
  );
}

/**
 * Derive User Nonce PDA
 * Seeds: ["user_nonce", user (32 bytes)]
 */
export function getUserNoncePda(
  user: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.USER_NONCE), user.toBuffer()],
    programId
  );
}

/**
 * Derive Position PDA
 * Seeds: ["position", owner (32 bytes), market (32 bytes)]
 */
export function getPositionPda(
  owner: PublicKey,
  market: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.POSITION), owner.toBuffer(), market.toBuffer()],
    programId
  );
}

/**
 * Return mints in the canonical order used by orderbook PDAs.
 */
export function canonicalMintPair(
  mintA: PublicKey,
  mintB: PublicKey
): [PublicKey, PublicKey] {
  return Buffer.compare(mintA.toBuffer(), mintB.toBuffer()) <= 0
    ? [mintA, mintB]
    : [mintB, mintA];
}

/**
 * Derive Orderbook PDA
 * Seeds: ["orderbook", canonical_mint_a (32 bytes), canonical_mint_b (32 bytes)]
 */
export function getOrderbookPda(
  mintA: PublicKey,
  mintB: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  const [canonicalMintA, canonicalMintB] = canonicalMintPair(mintA, mintB);
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.ORDERBOOK),
      canonicalMintA.toBuffer(),
      canonicalMintB.toBuffer(),
    ],
    programId
  );
}

/**
 * Derive Address Lookup Table PDA
 * Seeds: [authority (32 bytes), recent_slot (u64 little-endian)]
 * Program: ALT_PROGRAM_ID
 */
export function getAltPda(
  authority: PublicKey,
  recentSlot: bigint
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [authority.toBuffer(), toU64Le(recentSlot)],
    ALT_PROGRAM_ID
  );
}

/**
 * Derive GlobalDepositToken whitelist PDA.
 * Seeds: ["global_deposit", mint]
 */
export function getGlobalDepositTokenPda(
  mint: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.GLOBAL_DEPOSIT), mint.toBuffer()],
    programId
  );
}

/**
 * Derive User Global Deposit PDA.
 * Seeds: ["global_deposit", user, mint]
 */
export function getUserGlobalDepositPda(
  user: PublicKey,
  mint: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.GLOBAL_DEPOSIT), user.toBuffer(), mint.toBuffer()],
    programId
  );
}

/**
 * Derive position ALT PDA used by initPositionTokens.
 * Seeds: [position, recent_slot]
 * Program: ALT_PROGRAM_ID
 */
export function getPositionAltPda(
  position: PublicKey,
  recentSlot: bigint
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [position.toBuffer(), toU64Le(recentSlot)],
    ALT_PROGRAM_ID
  );
}

/**
 * Collection of all PDA functions for easy access
 */
export const pda = {
  getExchangePda,
  getMarketPda,
  getConditionTombstonePda,
  getVaultPda,
  getMintAuthorityPda,
  getConditionalMintPda,
  getAllConditionalMintPdas,
  getMplMetadataPda,
  getOrderStatusPda,
  getUserNoncePda,
  getPositionPda,
  canonicalMintPair,
  getOrderbookPda,
  getAltPda,
  getGlobalDepositTokenPda,
  getUserGlobalDepositPda,
  getPositionAltPda,
};
